/**
 * A section's ⚙ panel — the website twin of the transaction field panel
 * (BuildFieldPanel): the shared AnchoredPanel shell, PanelHeader + PanelTabs,
 * and a footer of Delete · Copy … Done. What it changes shows on the page at
 * once (the caller draws the draft); Done stores it as ONE undo step; Esc or a
 * click outside drops it.
 */

import { useMemo, useState } from 'react'
import { Copy, Trash2 } from 'lucide-react'
import {
  AnchoredPanel, ChipGroup, PanelHeader, PanelNote, PanelTabs, VioletButton,
} from '@twasta/page-runtime/customize'
import type { PageModel, Section } from '@/pages/designers/page/types'
import {
  blockSpec, DEVICES, devicesOf, PLAIN, replaceBlock, sectionBlocks, sectionLabel, SPACING, spacingOf,
  WIDTHS, withDevices, withSpacing,
} from './pageOps'
import { BlockFields, type PageOption } from './fields'

type Tab = 'look' | 'content'

export default function SectionPanel({
  anchor, section, page, pages, dirty, onChange, onDone, onCancel, onDelete, onCopy, designerHref,
}: {
  anchor: HTMLElement | null
  section: Section
  page: PageModel
  pages: PageOption[]
  /** Whether the panel holds changes (Done stores them). */
  dirty: boolean
  onChange: (next: Section) => void
  onDone: () => void
  onCancel: () => void
  onDelete: () => void
  onCopy: () => void
  designerHref: string
}) {
  const [tab, setTab] = useState<Tab>('look')
  const name = sectionLabel(section)
  const blocks = useMemo(() => sectionBlocks(section), [section])
  const editable = blocks.filter((b) => blockSpec(b.type))
  const other = blocks.filter((b) => !blockSpec(b.type) && !['columns', 'container', 'divider', 'spacer'].includes(b.type))
  const schemes = page.designTokens?.colorSchemes || []
  const index = (page.sections || []).findIndex((s) => s.id === section.id)
  const sub = `Section ${index + 1} of ${(page.sections || []).length} on this page`

  return (
    <AnchoredPanel anchor={anchor} onClose={onCancel} label={`${name} section`} width={420}>
      <PanelHeader title={`${name} section`} sub={sub} />
      <PanelTabs<Tab>
        tabs={[{ id: 'look', label: 'Look' }, { id: 'content', label: 'Content', count: editable.length || undefined }]}
        value={tab} onChange={setTab} />
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {tab === 'look' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Background</span>
              <ChipGroup label="Background" hook="site-edit-background"
                options={[{ value: PLAIN.id, label: PLAIN.label }, ...schemes.map((s) => ({ value: s.id, label: s.name || s.id }))]}
                value={section.colorSchemeId || ''}
                onChange={(v) => {
                  const next = { ...section }
                  if (v) next.colorSchemeId = v
                  else delete next.colorSchemeId
                  onChange(next)
                }} />
              {schemes.length === 0 && (
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>
                  This website’s style has no colour bands yet — add them in ⚙ Website style’s theme.
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Space above and below</span>
              <ChipGroup label="Space above and below" hook="site-edit-spacing"
                options={SPACING.map((s) => ({ value: s.id, label: s.label }))}
                value={spacingOf(section)} onChange={(v) => onChange(withSpacing(section, v))} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Width</span>
              <ChipGroup label="Width" hook="site-edit-width"
                options={WIDTHS.map((w) => ({ value: w.id, label: w.label }))}
                value={section.width === 'full' || section.width === 'breakout' ? 'full' : 'contained'}
                onChange={(v) => onChange({ ...section, width: v as Section['width'] })} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Show it on</span>
              <ChipGroup label="Show it on" hook="site-edit-devices"
                options={DEVICES.map((d) => ({ value: d.id, label: d.label }))}
                value={devicesOf(section)} onChange={(v) => onChange(withDevices(section, v))} />
            </div>
          </>
        )}

        {tab === 'content' && (
          <>
            <PanelNote>Change the words here; they show on the page as you go.</PanelNote>
            {editable.map((b) => (
              <BlockFields key={b.id} block={b} pages={pages} showTitle={editable.length > 1}
                onChange={(nb) => onChange(replaceBlock(section, b.id, nb))} />
            ))}
            {editable.length === 0 && <PanelNote>This section has nothing to type in here.</PanelNote>}
            {other.length > 0 && (
              <PanelNote>
                {other.length === 1 ? 'One part' : `${other.length} parts`} of this section
                ({Array.from(new Set(other.map((b) => b.type.replace(/_/g, ' ')))).join(', ')}) are changed in the{' '}
                <a href={designerHref} target="_top" rel="noreferrer" style={{ color: '#6d28d9', fontWeight: 600 }}>page designer</a>.
              </PanelNote>
            )}
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap', paddingTop: 6, borderTop: '1px solid var(--color-border, #f1f3f6)' }}>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <button type="button" onClick={onDelete} data-tw-action="site-edit-delete-section"
              style={{ height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#b42318', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Trash2 size={14} /> Delete this section
            </button>
            <VioletButton quiet hook="site-edit-copy-section" onClick={onCopy}><Copy size={14} /> Copy</VioletButton>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>
              {dirty ? 'Esc or a click outside drops them' : 'No changes yet'}
            </span>
            <VioletButton hook="site-edit-panel-done" onClick={onDone}>Done</VioletButton>
          </span>
        </div>
      </div>
    </AnchoredPanel>
  )
}
